import { CreditCard, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApiError } from '../lib/api.ts';
import type { CouleurDepart, LicenceDraft } from '../lib/licence.ts';
import { COULEURS_DEPART, deleteLicence, getLicence, saveLicence } from '../lib/licence.ts';

const VIDE: LicenceDraft = {
    number: '', category: null, season: null, clubName: null, clubNumber: null,
    subscriber: false, ranking: null, teeColour: null,
};

/** Un champ de texte de la carte. Vide vaut absent, pas chaîne vide. */
function Champ({ mot, exemple, valeur, onEcrire, taille = 40 }: {
    mot: string;
    exemple: string;
    valeur: string | null;
    onEcrire: (v: string | null) => void;
    taille?: number;
}) {
    return (
        <label className="block">
            <span className="mb-1 block text-sm font-medium">{mot}</span>
            <input type="text" maxLength={taille} placeholder={exemple}
                className="input input-bordered w-full"
                value={valeur ?? ''}
                onChange={(e) => onEcrire(e.target.value.trim() === '' ? null : e.target.value)} />
        </label>
    );
}

/* La saisie de la licence, dans les préférences.
 *
 * ── Pourquoi à la main ──────────────────────────────────────────────────────
 *
 * La fédération n'ouvre aucune interface publique pour lire la fiche d'un
 * licencié. Aller chercher ces données dans son application ne serait ni
 * autorisé ni durable — les points d'entrée privés changent sans préavis — et
 * un index associé à un nom et un numéro est une donnée personnelle. Recopier
 * sa carte une fois par saison prend deux minutes et ne dépend de personne.
 *
 * ── Ce qui n'est pas demandé ────────────────────────────────────────────────
 *
 * Le nom, déjà sur le compte. Et le « niveau de jeu » de la carte, qui est
 * l'index : il se saisit juste au-dessus, dans sa propre carte, avec son
 * historique. Le redemander ici en ferait deux valeurs à tenir d'accord.
 */
export function LicenceCard() {
    const [form, setForm] = useState<LicenceDraft | null>(null);
    const [existe, setExiste] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [ok, setOk] = useState(false);
    /* Le joueur a-t-il décidé lui-même de son statut ?
     *
     * Tant que non, saisir un club le pose à « abonné » : c'est le cas de
     * loin le plus fréquent, et cela évite une question de plus. Dès qu'il y
     * touche, on ne décide plus rien à sa place — un indépendant RATTACHÉ à un
     * club a bien un club sur sa carte sans en être abonné, et lui écrire
     * « abonné » serait une erreur de fait sur une pièce d'identité. */
    const [statutChoisi, setStatutChoisi] = useState(false);

    useEffect(() => {
        void getLicence()
            .then((l) => {
                if (l) { setForm({ ...l }); setExiste(true); } else { setForm({ ...VIDE }); }
            })
            .catch(() => setForm({ ...VIDE }));
    }, []);

    if (!form) return null;

    const set = <K extends keyof LicenceDraft>(clef: K, v: LicenceDraft[K]): void =>
        setForm((f) => {
            if (f == null) return f;
            const suite = { ...f, [clef]: v };
            // Le premier club saisi propose « abonné », et rien de plus : la
            // valeur est visible à l'écran, et se corrige d'un appui.
            if (clef === 'clubName' && !statutChoisi) suite.subscriber = v != null;
            return suite;
        });

    const enregistrer = async (): Promise<void> => {
        setBusy(true); setError(null);
        try {
            const l = await saveLicence(form);
            setForm({ ...l });
            setExiste(true);
            setOk(true);
            setTimeout(() => setOk(false), 2000);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Enregistrement impossible');
        } finally { setBusy(false); }
    };

    const effacer = async (): Promise<void> => {
        setBusy(true); setError(null);
        try {
            await deleteLicence();
            setForm({ ...VIDE });
            setExiste(false);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Suppression impossible');
        } finally { setBusy(false); }
    };

    return (
        <section className="rounded-box bg-base-200 p-4">
            <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold">
                <CreditCard size={18} className="text-primary" /> Ma licence
            </h2>
            <p className="mb-3 text-xs opacity-60">
                Recopiez votre carte fédérale : elle s’affichera dans Licence, code-barres
                compris, et sans réseau.
            </p>

            {error && <div role="alert" className="alert alert-error mb-3 text-sm"><span>{error}</span></div>}
            {ok && <div role="status" className="alert alert-success mb-3 text-sm"><span>Licence enregistrée.</span></div>}

            <div className="space-y-3">
                <label className="block">
                    <span className="mb-1 block text-sm font-medium">Numéro de licence</span>
                    {/* `inputMode numeric` sans `type number` : le numéro peut
                        porter des lettres sur certaines licences, et un champ
                        numérique refuserait la frappe tout en masquant pourquoi.
                        Le clavier chiffré s'ouvre quand même. */}
                    <input type="text" inputMode="numeric" maxLength={24}
                        placeholder="123 456 789"
                        className="input input-bordered w-full font-score tracking-wider"
                        value={form.number}
                        onChange={(e) => set('number', e.target.value)} />
                    <span className="mt-1 block text-xs opacity-60">
                        Les espaces sont ignorés. C’est ce numéro que porte le code-barres.
                    </span>
                </label>

                <div className="grid grid-cols-2 gap-3">
                    <Champ mot="Catégorie" exemple="Adulte" valeur={form.category}
                        onEcrire={(v) => set('category', v)} />
                    <Champ mot="Saison" exemple="2026/2027" valeur={form.season} taille={20}
                        onEcrire={(v) => set('season', v)} />
                </div>

                <Champ mot="Club" exemple="Golf de la Vallée" valeur={form.clubName} taille={80}
                    onEcrire={(v) => set('clubName', v)} />

                <div className="grid grid-cols-2 gap-3">
                    <Champ mot="Numéro du club" exemple="1234" valeur={form.clubNumber} taille={20}
                        onEcrire={(v) => set('clubNumber', v)} />
                    <Champ mot="Classement" exemple="Non classé" valeur={form.ranking}
                        onEcrire={(v) => set('ranking', v)} />
                </div>

                {/* Le statut ne se pose QUE s'il y a un club.
                    Sans club, la question n'a pas d'objet : on est indépendant
                    et non rattaché, ce que la carte n'a rien à dire.

                    Deux boutons nommés, et non plus une case à cocher « Abonné
                    golf » : l'étiquette seule n'apprenait à personne ce qu'elle
                    engageait, et une case décochée ne disait pas « indépendant »,
                    elle ne disait rien. */}
                {form.clubName != null && (
                    <div>
                        <span className="mb-1 block text-sm font-medium">
                            Statut dans ce club
                        </span>
                        <div className="grid grid-cols-2 gap-2">
                            {([[true, 'Abonné'], [false, 'Indépendant']] as const).map(([v, mot]) => (
                                <button key={mot} type="button"
                                    aria-pressed={form.subscriber === v}
                                    onClick={() => { setStatutChoisi(true); set('subscriber', v); }}
                                    className={`rounded-field px-2 py-3 text-sm font-semibold transition-colors ${
                                        form.subscriber === v
                                            ? 'bg-primary text-primary-content' : 'bg-base-100 hover:bg-base-300'
                                    }`}>
                                    {mot}
                                </button>
                            ))}
                        </div>
                        <span className="mt-1 block text-xs opacity-60">
                            Un indépendant peut être rattaché à un club sans en être abonné :
                            il ne peut alors pas le représenter en compétition par équipes.
                        </span>
                    </div>
                )}

                <div>
                    <span className="mb-1 block text-sm font-medium">Départ recommandé</span>
                    {/* Les vraies couleurs des piquets, et non celles du thème :
                        c'est la seule famille de teintes de l'application qui ne
                        lui appartient pas. Le nom accompagne la pastille — un
                        choix qui ne tiendrait qu'à la couleur exclurait ceux qui
                        ne distinguent pas le rouge du vert. */}
                    <div className="grid grid-cols-4 gap-2">
                        <button type="button"
                            aria-pressed={form.teeColour == null}
                            onClick={() => set('teeColour', null)}
                            className={`rounded-field px-2 py-2 text-xs font-semibold transition-colors ${
                                form.teeColour == null ? 'bg-primary text-primary-content' : 'bg-base-100'
                            }`}>
                            Aucun
                        </button>
                        {(Object.keys(COULEURS_DEPART) as CouleurDepart[]).map((c) => {
                            const d = COULEURS_DEPART[c];
                            const actif = form.teeColour === c;
                            return (
                                <button key={c} type="button"
                                    aria-pressed={actif}
                                    onClick={() => set('teeColour', c)}
                                    className={`rounded-field px-2 py-2 text-xs font-bold ring-1 ring-black/15
                                                transition-transform ${actif ? 'scale-105 ring-2 ring-primary' : ''}`}
                                    style={{ background: d.pigment, color: d.encre }}>
                                    {d.mot}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <button type="button" disabled={busy || form.number.trim() === ''}
                    onClick={() => void enregistrer()}
                    className="btn btn-primary btn-block tap-target">
                    Enregistrer ma licence
                </button>

                {existe && (
                    <button type="button" disabled={busy} onClick={() => void effacer()}
                        className="btn btn-ghost btn-sm gap-2 text-error">
                        <Trash2 size={16} /> Retirer ma licence
                    </button>
                )}
            </div>
        </section>
    );
}
